import { computed, onBeforeUnmount, ref } from 'vue'

interface LoadingStep {
  key: string
  labelKey: string
  /** Milliseconds after the search starts when this step becomes active. */
  delay: number
}

// Fixed-timer steps that reveal the search progressing. The first step (delay 0) shows immediately;
// later steps appear on their timers unless the request resolves first.
const LOADING_STEPS: LoadingStep[] = [
  { key: 'searching', labelKey: 'conversations.filters.ai_search.loading_searching', delay: 0 },
  { key: 'interpreting', labelKey: 'conversations.filters.ai_search.loading_interpreting', delay: 1000 },
  { key: 'matching', labelKey: 'conversations.filters.ai_search.loading_matching', delay: 2500 },
]

/**
 * Drives the progressive loading message shown while an AI filter prompt is being searched.
 * `start()` (re)schedules the steps from the beginning; `stop()` cancels any pending steps.
 */
export function useProgressiveLoading() {
  const stepIndex = ref(0)
  const loadingStepLabelKey = computed(() => LOADING_STEPS[stepIndex.value].labelKey)

  let timers: ReturnType<typeof setTimeout>[] = []

  const stop = () => {
    timers.forEach(clearTimeout)
    timers = []
  }

  const start = () => {
    stop()
    stepIndex.value = 0
    timers = LOADING_STEPS.slice(1).map((step, i) => setTimeout(() => (stepIndex.value = i + 1), step.delay))
  }

  onBeforeUnmount(stop)

  return { loadingStepLabelKey, start, stop }
}
